<template>
  <div v-if="subAccountInfo" class="p-sub-account-detail">
    <div class="p-sub-account-detail-form">
      <plv-form
        ref="form"
        :model="form"
        :rules="rules"
        form-width="600px"
        label-width="110px"
      >
        <plv-form-item
          label="分账号邮箱"
          label-height="16px"
        >
          <div class="p-sub-account-detail-item">{{ subAccountInfo.email }}</div>
        </plv-form-item>
        <plv-form-item
          label="密码"
          label-height="16px"
        >
          <div class="p-sub-account-detail-item">
            <span>******</span>
            <span class="p-link-btn" @click="handleResetPswModel">重置密码</span>
          </div>
        </plv-form-item>
        <plv-form-item
          label="联系人"
          props="contacts"
        >
          <plv-input
            v-model.trim="form.contacts"
            size="medium"
            placeholder="请输入联系人姓名"
            :en-length="1"
            :maxlength="50"
          />
        </plv-form-item>
        <plv-form-item
          prop="phone"
          label="手机号码"
        >
          <plv-input
            v-model="form.phone"
            size="medium"
            placeholder="请输入联系人手机号码"
          />
        </plv-form-item>
        <plv-form-item
          prop="memo"
          label="备注"
        >
          <plv-input
            v-model.trim="form.memo"
            size="medium"
            placeholder="请输入备注"
            :en-length="1"
            :maxlength="50"
          />
        </plv-form-item>
        <plv-form-item
          v-if="showBalanceInput"
          prop="balance"
          label="金额"
          :tips-text="isSubmiting ? '' : isResourceLimit ? $_getBalanceTip(form.balance) : '请输入分配金额，无延迟和研讨会使用均依赖于金额'"
        >
          <plv-input-number
            v-model="form.balance"
            size="medium"
            placeholder="请输入金额"
            :en-length="1"
            :max="100000000"
          />
          <div class="p-sub-account-detail__balance-info">
            <span>剩余金额：{{ currentBalance | decimalFormat }} 元</span>
          </div>
        </plv-form-item>
        <plv-form-item
          v-if="showResourcePointInput"
          prop="resourcePoint"
          label="资源点"
          :tips-text="isSubmiting ? '' : $_getResourcePointTip()"
        >
          <plv-input-number
            v-model="form.resourcePoint"
            size="medium"
            placeholder="请输入资源点"
            :precision="0"
            :min="0"
            :max="100000000"
          />
          <div class="p-sub-account-detail__balance-info">
            <span>剩余资源点：{{ totalResourcePoint }}</span>
          </div>
        </plv-form-item>
        <plv-form-item
          v-if="waitPayAmount > 0"
          label="待支付"
          label-height="16px"
        >
          <span class="p-sub-account-detail__wait-pay" @click="handleToBusinessUsage">
            待支付总额：{{ waitPayAmount | decimalFormat }} 元（设置不低于待支付金额的数值，系统将自动抵扣待支付账单）
          </span>
        </plv-form-item>
        <plv-form-item label="账号到期时间" label-height="40px">
          <plv-radio-group v-model="form.expireType">
            <plv-radio value="group">跟随主账号</plv-radio>
            <plv-radio value="custom">
              自定义日期
            </plv-radio>
            <plv-date-picker
              v-model="form.expireDate"
              value-format="timestamp"
              disabled-before-now
              :disabled-date="getDisabledDate"
            />
          </plv-radio-group>
        </plv-form-item>
        <plv-form-item>
          <plv-button @click="handleSubmit">保存</plv-button>
        </plv-form-item>
      </plv-form>
    </div>
    <reset-psw-modal :visible.sync="resetPswVisible" @resetPassword="handleResetPassword" />
  </div>
</template>

<script>
import { defaultFormRules } from '@/utils/type';
import { sleep } from '@/utils/utils';
import accountInfoMixin from '@/mixins/accountInfo';
import ResetPswModal from '@component/resetPswModal/resetPswModal';
import { mapGetters, mapState } from 'vuex';
import { ynToBool } from '@polyv/utils/boolean';

export default {
  components: {
    ResetPswModal
  },
  mixins: [accountInfoMixin],
  data() {
    return {
      form: {
        unionId: '',
        contacts: '',
        phone: '',
        memo: '',
        balance: 0,
        resourcePoint: 0,
        expireType: '',
        expireDate: ''
      },
      resetPswVisible: false,
      isSubmiting: false,
      rules: {
        ...defaultFormRules,
        phone: [
          { type: 'string', message: '请输入联系人手机号码', required: true },
          { pattern: /^\d+$/, message: '手机号格式错误' },
        ],
      },
    };
  },
  computed: {
    ...mapState({
      accountInfo: state => state.account.accountInfo
    }),
    ...mapGetters({
      subAccountInfo: 'account/subAccountInfo'
    }),
    currentId() {
      return this.$route.params.id;
    },
    currentBalance() {
      return Number(this.subAccountInfo?.balance || 0);
    },
    waitPayAmount() {
      return Number(this.subAccountInfo?.waitPayAmount || 0);
    },
    // 集团分配来源剩余：分配与回收只作用于该来源，表单以它为目标值基准
    currentResourcePoint() {
      return Number(this.subAccountInfo?.allocatedResourcePoint || 0);
    },
    // 分账号资源点总余额（含合同充值/POP赠送等其它来源），仅展示
    totalResourcePoint() {
      return Number(this.subAccountInfo?.resourcePoint || 0);
    },
    // 剩余可分配资源点（仅用量限制开启时作为提示与拦截依据）
    assignableResourcePoint() {
      return Number(this.overViewInfo?.assignableResourceVO?.resourcePointAssignable || 0);
    },
    // 主账号资源点结算模式
    isResourcePointSettlement() {
      return ynToBool(this.overViewInfo?.resourcePointSettlement || 'N');
    },
    // 主账号关闭资源点结算模式时始终展示金额框；开启时分账号仍有金额余额才展示
    showBalanceInput() {
      return !this.isResourcePointSettlement || this.currentBalance > 0;
    },
    // 主账号开启资源点结算模式时始终展示资源点框；关闭时分账号仍有资源点余额才展示
    showResourcePointInput() {
      return this.isResourcePointSettlement || this.totalResourcePoint > 0;
    },
  },
  watch: {
    subAccountInfo: {
      handler(value) {
        this.form.phone = value.phone ?? '';
        this.form.memo = value.memo ?? '';
        this.form.balance = value.balance ?? 0;
        this.form.resourcePoint = value.allocatedResourcePoint ?? 0;
        this.form.unionId = value.unionId;
        this.form.contacts = value.contacts;
        this.form.expireType = value.expireType;
        this.form.expireDate = value.expireDate;
      },
      immediate: true,
    },
    'form.expireType'(value) {
      if (value === 'group') {
        this.form.expireDate = '';
      }
      console.info(value);
    }
  },
  async created() {
    const subAccountInfo = await this.$store.dispatch('account/getSubAccountInfo', this.currentId);
    this.$store.commit('account/setSubAccountInfo', subAccountInfo);
  },
  mounted() {
    this.$store.dispatch('account/getOverViewInfo');
  },
  methods: {
    getDisabledDate(date) {
      // 不得高于帐号的过期时间
      const liveEndDate = this.accountInfo.liveEndDate || 0;
      const vodEndDate = this.accountInfo.vodEndDate || 0;
      const amountEndDate = this.accountInfo.amountEndDate || 0;
      const endDate = Math.max(liveEndDate, vodEndDate, amountEndDate);
      if (endDate) {
        return date.getTime() > endDate;
      }
      return false;
    },
    // 用量限制开启时提示还可分配多少，关闭时不展示可分配（与金额一致）
    $_getResourcePointTip() {
      if (!this.isResourceLimit) {
        return '请输入资源点，仅非主营计费项（连麦、研讨会等）可用资源点结算';
      }
      const diff = Number(this.form.resourcePoint || 0) - this.currentResourcePoint;
      if (diff > this.assignableResourcePoint) {
        return `超出主账号剩余可分配资源点（${this.assignableResourcePoint}）`;
      }
      return `主账号剩余可分配资源点：${this.assignableResourcePoint}`;
    },
    async handleSubmit() {
      try {
        this.$emit('loading');
        this.isSubmiting = true;
        await this.$refs.form.validate();
        if (this.isResourceLimit) {
          const diff = Number(this.form.resourcePoint || 0) - this.currentResourcePoint;
          if (diff > this.assignableResourcePoint) {
            this.$toast.error(`分配资源点超出主账号剩余可分配资源点（${this.assignableResourcePoint}）`);
            return;
          }
        }
        await this.$store.dispatch('account/updateSubAccountInfo', this.form);
        const updatedInfo = Object.assign(this.subAccountInfo, this.form);
        this.$store.commit('account/setSubAccountInfo', updatedInfo);
        // 后端重新设置有延迟
        await sleep(2 * 1000);
        await this.$store.dispatch('account/getOverViewInfo');
        this.$toast.success('保存成功');
      } finally {
        this.isSubmiting = false;
        this.$emit('cancelLoading');
      }
    },
    handleResetPswModel() {
      this.resetPswVisible = true;
    },
    handleToBusinessUsage() {
      this.$router.push({
        path: `/group/subAccount/${this.currentId}/businessUsage`,
        query: { status: 6 }
      });
    },
    async handleResetPassword(form) {
      this.form.password = form.password;
      try {
        await this.$store.dispatch('account/updateSubAccountInfo', this.form);
        this.$toast.success('修改成功');
      } catch {}
      this.resetPswVisible = false;
    }
  }
};
</script>

<style lang="scss" scoped>
.p-sub-account-detail {
  .p-sub-account-detail__balance-info {
    margin-top: 8px;
    color: #757575;
    font-size: 14px;
  }
  .p-sub-account-detail__wait-pay {
    color: #f56c3d;
    cursor: pointer;
  }
  .p-link-btn {
    margin-left: 5px;
    color: #2196f3;
    cursor: pointer;
    font-size: 14px;
    &:active {
      opacity: .7;
    }
  }
  .plv-date-picker {
    margin-left: 10px !important;
  }
  .plv-radio-group {
    align-items: center;
  }
}
</style>
